<script>
import { mapGetters } from 'vuex'
import { USER_CONSTS as UC } from '@/stores/consts'
import {
  ANALYTIC_COMPARISON_RISK_SCORE_PLOT_TYPE_ID,
  ANALYTIC_TRENDS_RISK_SCORE_PLOT_TYPE_ID,
  CONDITION_MONITORING_REPORT_DATA_TRENDS_PLOT_TYPE_ID,
  FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID,
  FLIGHT_PARAMETER_TRENDS_PLOT_TYPE_ID,
  REPORT_PARAMETER_TRENDS_PLOT_TYPE_ID,
  PLOT_TYPES,
  PLOT_UPDATE_MESSAGES,
} from '@/utilities/appConsts'
import {
  getDateSelectorEndDateGivenOffset,
  getDateSelectorStartDateGivenOffset,
} from '@/utilities/dates'
import { mergeFieldsSelections } from '@/utilities/filters'
import {
  createNewPagePlotGivenPlotTypeId,
  duplicateChartData,
  duplicatePagePlot,
  getFieldsGivenPlotTypeId,
  getNextHighchartColor,
} from '@/utilities/plotting'
import * as htmlToImage from 'html-to-image'
import _cloneDeep from 'lodash/cloneDeep'
import { v4 as uuidv4 } from 'uuid'

export default {
  data() {
    return {
      isLookupDataLoading: false,
      isPlotApplying: false,
      isPlotAssociating: false,
      isPlotSaving: false,
      isPlotDeleting: false,
      savedPlots: [],
      pagePlots: [],
      chartData: [],
      loadingPlotIds: [],
      savedPlotId: null,
      savedPlotTitle: '',
      isPlotPreviewLoading: false,
      isPlotTypeDialogLoading: false,
      plotPreviewDialogVisible: false,
      plotTypeDialogVisible: false,
      dupeNameDialogVisible: false,
      pagePlotName: '',
      filterLookups: [],
      imageSource: '',
      tabIndex: 0,
      showDeletePlotToast: false,
      deletePlotToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Plot deleted',
          position: 'bottom',
          dismissible: false,
          hasToastAction: true,
          toastActionLabel: 'Undo',
          autoClose: true,
          icon: 'mdi-delete',
          duration: 12000,
        },
      ],
      showSavePlotToast: false,
      savePlotToastDefinition: [
        {
          mode: 'toast',
          type: 'success',
          text: 'Plot saved',
          position: 'bottom',
          dismissible: true,
          autoClose: true,
        },
      ],
    }
  },

  computed: {
    ...mapGetters(UC.NAMESPACE, [
      'flightDataAnalyticComparisonPermission',
      'flightDataFlightComparisonPermission',
      'flightDataAnalyticTrendsPermission',
      'flightDataFlightTrendsPermission',
      'flightDataAcmfTrendsPermission',
    ]),
    isDataLoading() {
      return this.isLookupDataLoading
    },

    getPlotTypes() {
      const updatedPlotTypes = PLOT_TYPES.map(plotType => {
        return this.enablePlotTypeWithPermission(plotType)
      })
      return (
        updatedPlotTypes
          .filter(item => item.enabled && this.isValidPlotTypeId(item.id))
          .sort((a, b) => a.order - b.order) || []
      )
    },

    getPagePlots() {
      const updatedPagePlots = this.pagePlots.map((pagePlot, index) => {
        const tabs = pagePlot.options.data.tabs.map(tab => {
          const fields = getFieldsGivenPlotTypeId(pagePlot.plotTypeId)
          const selections = tab.selection
          const mergedFields = mergeFieldsSelections(fields, selections)

          return {
            ...tab,
            disabled: !!tab.prepopulated,
            tabDisabled: !!tab.prepopulated,
            error: this.isTabInvalid(tab),
            title: this.getTabTitle(tab, mergedFields),
            selection: mergedFields,
          }
        })

        pagePlot.index = index
        const updatedPagePlot = pagePlot
        updatedPagePlot.caption = pagePlot.caption
        updatedPagePlot.options.data.tabs = tabs
        updatedPagePlot.options.data.tabsInvalid = this.areTabsInvalid(tabs)
        updatedPagePlot.maintenanceIssueIds = pagePlot.maintenanceIssueIds || []
        updatedPagePlot.globalFilterValid = updatedPagePlot.prepopulated
          ? true
          : this.isGlobalFilterValid(pagePlot)
        updatedPagePlot.options.separateCharts =
          pagePlot.options?.separateCharts || false
        updatedPagePlot.prepopulated = pagePlot.prepopulated || false
        updatedPagePlot.isExternallyVisible =
          pagePlot.isExternallyVisible || false

        return updatedPagePlot
      })

      return updatedPagePlots
    },
  },

  watch: {
    pagePlots: {
      deep: true,
      handler() {
        const eccnName = this.getEccnNameGivenMaxRank()
        if (this.handleEccnNameUpdate) {
          this.handleEccnNameUpdate(eccnName)
        }
      },
    },
  },

  methods: {
    isTabInvalid(tab) {
      let invalid = false
      tab.selection.forEach(item => {
        if (!invalid && item.selection) {
          if (item.selection.length < item.validationCount) {
            invalid = true
          }
        }
      })
      return invalid
    },

    areTabsInvalid(tabs) {
      return tabs.filter(item => item.error).length > 0
    },

    isGlobalFilterValid(pagePlot) {
      // get page plot airlineIds and masterModelIds
      const tabs = pagePlot.options.data.tabs
      const ppAirlineIds = []
      const ppMasterModelIds = []

      tabs.forEach(tab => {
        const selections = tab.selection
        selections.forEach(selection => {
          if (selection.selectedItems) {
            const selectedItems = selection.selectedItems
            selectedItems.forEach(selectedItem => {
              if (selectedItem) {
                const keys = Object.keys(selectedItem)
                keys.forEach((key, index) => {
                  if (key === 'airlineId') {
                    const airlineId = Object.values(selectedItem)[index]
                    ppAirlineIds.push(airlineId)
                  }
                  if (key === 'masterModelId') {
                    const masterModelId = Object.values(selectedItem)[index]
                    ppMasterModelIds.push(masterModelId)
                  }
                })
              }
            })
          }
        })
      })

      // get global filter airlineIds and masterModelIds
      const gfAirlineIds = this.airlineIdsSelected || []
      const gfMasterModelIds = this.masterModelIdsSelected || []

      // check if all page plot airlineIds are currently in global filter airlineIds
      const airlineIdsFound =
        ppAirlineIds.length === 0 ||
        gfAirlineIds.length === 0 ||
        ppAirlineIds.some(r => gfAirlineIds.indexOf(r) >= 0)

      // check if all page plot masterModelIds are currently in global filter masterModelids
      const masterModelIdsFound =
        ppMasterModelIds.length === 0 ||
        gfMasterModelIds.length === 0 ||
        ppMasterModelIds.some(r => gfMasterModelIds.indexOf(r) >= 0)

      return airlineIdsFound && masterModelIdsFound
    },

    getEccnNameGivenMaxRank() {
      // build eccns
      const eccns = []
      this.pagePlots.forEach(pagePlot => {
        const tabs = pagePlot.options.data.tabs
        tabs.forEach(tab => {
          if (tab.hidden === false) {
            const filters = tab.selection
            filters.forEach(filter => {
              const detailFilters = filter.selectedItems || []
              detailFilters.forEach(detailFilter => {
                if (
                  detailFilter &&
                  detailFilter.eccnName &&
                  detailFilter.eccnRank
                ) {
                  eccns.push({
                    eccnName: detailFilter.eccnName,
                    eccnRank: detailFilter.eccnRank,
                  })
                }
              })
            })
          }
        })
      })

      // sort eccns
      const sorted = eccns.sort((a, b) => b.eccnRank - a.eccnRank)
      // get first item
      const [first] = sorted

      return first ? first.eccnName : ''
    },

    getPagePlotGivenPlotId(plotId) {
      const [pagePlot] = this.pagePlots.filter(item => item.id === plotId)
      return pagePlot
    },

    getPagePlotServerIdGivenPlotId(plotId) {
      const [pagePlot] = this.pagePlots.filter(item => item.id === plotId)
      return pagePlot ? pagePlot.serverId : null
    },

    getSavedPlotServerIdGivenPlotId(plotId) {
      const [pagePlot] = this.savedPlots.filter(item => item.id === plotId)
      return pagePlot ? pagePlot.serverId : null
    },

    getSavedPlotGivenSavedPlotId() {
      const [pagePlot] = this.savedPlots.filter(
        item => item.id === this.savedPlotId,
      )
      return pagePlot || null
    },

    getTabGivenTabId(plotId, tabId) {
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        const [tab] = pagePlot.options.data.tabs.filter(
          item => item.id === tabId,
        )
        return tab
      }
      return null
    },

    getSelectionsGivenPlotIdTabId(plotId, tabId) {
      const tab = this.getTabGivenTabId(plotId, tabId)
      if (tab) {
        return tab?.selection || []
      }
      return []
    },

    enablePlotTypeWithPermission(plotType) {
      switch (plotType.id) {
        case ANALYTIC_TRENDS_RISK_SCORE_PLOT_TYPE_ID: {
          return this.updatePlotType(
            plotType,
            this.flightDataAnalyticTrendsPermission,
          )
        }
        case FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID: {
          return this.updatePlotType(
            plotType,
            this.flightDataFlightComparisonPermission,
          )
        }
        case ANALYTIC_COMPARISON_RISK_SCORE_PLOT_TYPE_ID: {
          return this.updatePlotType(
            plotType,
            this.flightDataAnalyticComparisonPermission,
          )
        }
        case FLIGHT_PARAMETER_TRENDS_PLOT_TYPE_ID: {
          return this.updatePlotType(
            plotType,
            this.flightDataFlightTrendsPermission,
          )
        }
        case CONDITION_MONITORING_REPORT_DATA_TRENDS_PLOT_TYPE_ID: {
          return this.updatePlotType(
            plotType,
            this.flightDataAcmfTrendsPermission,
          )
        }
        case REPORT_PARAMETER_TRENDS_PLOT_TYPE_ID: {
          return this.updatePlotType(plotType, plotType.enabled)
        }
      }
    },

    updatePlotType(plotType, plotEnabled) {
      return {
        ...plotType,
        enabled: plotEnabled,
      }
    },

    isValidPlotTypeId(id) {
      switch (id) {
        case CONDITION_MONITORING_REPORT_DATA_TRENDS_PLOT_TYPE_ID:
          return this.isAcmfUser
        default:
          return this.isAirlineUser
      }
    },

    async getPagePlotImageSrc(id) {
      const htmlId = `image-node-${id}`
      const node = document.getElementById(htmlId)
      return htmlToImage
        .toPng(node)
        .then(function (dataUrl) {
          return dataUrl
        })
        .catch(function (error) {
          console.error('Something went wrong saving image!', error)
          return ''
        })
    },

    resetPagePlots() {
      this.pagePlots = this.getPagePlots
    },

    handleDisableSubmit(plotId, tabId) {
      if (
        this.isLookupDataLoading ||
        this.isPlotApplying ||
        this.isPlotAssociating ||
        this.isPlotSaving ||
        this.isPlotDeleting ||
        this.isAddPlotToIssueLoading ||
        this.isPlotPreviewLoading ||
        this.isPlotTypeDialogLoading
      ) {
        return
      }
      this.chartData = this.chartData.filter(
        item => item.id !== plotId && item.tabId !== tabId,
      )
    },

    handleClosePagePlot(plotId) {
      this.chartData = this.chartData.filter(item => item.plotId !== plotId)
      this.pagePlots = this.pagePlots.filter(item => item.id !== plotId)
      this.loadingPlotIds = this.loadingPlotIds.filter(id => id !== plotId)
    },

    handleOnPlotTypeSelection(payload) {
      const newPagePlot = _cloneDeep(
        createNewPagePlotGivenPlotTypeId(payload.id),
      )
      this.pagePlots.unshift(newPagePlot)
    },

    handleChangeTabIndex(plotId, plotTypeId, tabIndex) {
      this.handleManualApplyPagePlot(
        plotId,
        plotTypeId,
        PLOT_UPDATE_MESSAGES.updatePlotTabIndex,
        tabIndex,
      )
    },

    handleUpdatePagePlotCaption(plotId, plotTypeId, caption) {
      this.handleManualApplyPagePlot(
        plotId,
        plotTypeId,
        PLOT_UPDATE_MESSAGES.updatePlotCaption,
        caption,
      )
    },

    handleTogglePagePlotVisibility(plotId, plotTypeId, visibility) {
      this.handleManualApplyPagePlot(
        plotId,
        plotTypeId,
        PLOT_UPDATE_MESSAGES.updatePlotVisibility,
        visibility,
      )
    },

    async handleManualApplyPagePlot(plotId, plotTypeId, event, options) {
      this.isPlotApplying = true

      if (event === PLOT_UPDATE_MESSAGES.updatePlotData) {
        this.resetPagePlots()
        await this.handleLoadData(plotId, plotTypeId, options)
      }

      if (event === PLOT_UPDATE_MESSAGES.updateAllPlotData) {
        const event = PLOT_UPDATE_MESSAGES.updatePlotData
        const pagePlot = this.getPagePlotGivenPlotId(plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.dataUpdated = new Date().getTime()
          const tabIds = pagePlot.options.data.tabs.map(tab => tab.id)
          await this.handleManualApplyPagePlot(
            plotId,
            plotTypeId,
            event,
            tabIds,
          )
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updatePlotMaintenanceIssueIds) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.maintenanceIssueIds = options
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updatePlotTabIndex) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.tabIndex = options
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updatePlotTitle) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.title = options
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updatePlotCaption) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.caption = options
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updatePlotVisibility) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.dataUpdated = new Date().getTime()
          pagePlot.plotVisible = options
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updatePlotFilters) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.dataUpdated = new Date().getTime()
          this.clearChartDateGivenPlotId(plotId, options)
          this.updateChartDateDateUpdated(plotId)
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updateChartAutoscale) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.dataUpdated = new Date().getTime()
          pagePlot.options.axes.autoscale = options
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updateChartSeparateCharts) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.dataUpdated = new Date().getTime()
          pagePlot.options.separateCharts = options
          this.applySeparateChartsToChartData(plotId, options)
          this.updateChartDateDateUpdated(plotId)
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updateChartZoom) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.dataUpdated = new Date().getTime()
          pagePlot.options.axes.zoom = options
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updateChartYAxisOverrides) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        if (pagePlot) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.dataUpdated = new Date().getTime()
          const currentOverrides = pagePlot.options?.axes?.overrides || []
          const updatedOverrides = this.updateOverridesGivenOptions(
            currentOverrides,
            options,
          )
          pagePlot.options.axes.overrides = updatedOverrides
          this.applyChartDataGivenOverrides()
          this.updateChartDateDateUpdated(plotId)
        }
      }

      if (event === PLOT_UPDATE_MESSAGES.updateDateRange) {
        const pagePlot = this.pagePlots.find(pp => pp.id === plotId)
        const event = PLOT_UPDATE_MESSAGES.updatePlotData
        if (pagePlot) {
          pagePlot.options.dateChip = options
          const tabIds = pagePlot.options.data.tabs.map(tab => tab.id)
          await this.handleManualApplyPagePlot(
            plotId,
            plotTypeId,
            event,
            tabIds,
          )
        }
      }

      this.isPlotApplying = false
    },

    updateChartDateDateUpdated(plotId) {
      this.chartData = this.chartData.map(item => {
        let dateUpdated = item.dateUpdated
        if (item.plotId === plotId) {
          dateUpdated = new Date()
        }
        return {
          ...item,
          dateUpdated,
        }
      })
    },

    updateOverridesGivenOptions(currentOverrides, newOverride) {
      const filteredOverrides = currentOverrides.filter(
        item => item.unit !== newOverride.unit,
      )
      if (newOverride.action === 'reset') {
        return filteredOverrides
      }
      if (newOverride.action === 'update') {
        const mappedOverride = {
          unit: newOverride.unit,
          min: newOverride.min,
          max: newOverride.max,
        }
        const concatOverrides = filteredOverrides.concat(mappedOverride)
        return concatOverrides
      }
    },

    isValidPlotTypeForOverrides(plotTypeId) {
      return (
        plotTypeId !== FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID &&
        plotTypeId !== ANALYTIC_COMPARISON_RISK_SCORE_PLOT_TYPE_ID
      )
    },

    applyChartDataGivenOverrides() {
      this.chartData = this.chartData.map(cd => {
        const pagePlot = this.getPagePlotGivenPlotId(cd.plotId)
        let data = cd.data
        if (pagePlot && pagePlot.options) {
          const overrides = pagePlot.options?.axes?.overrides || []
          if (this.isValidPlotTypeForOverrides(cd.plotTypeId)) {
            const [override] = overrides.filter(item => item.unit === cd.unit)
            if (override) {
              data = cd.rawData
                .filter(record => {
                  return (
                    record.point >= override.min && record.point <= override.max
                  )
                })
                .map(record => {
                  return [record.timestamp, record.point]
                })
            } else {
              data = cd.rawData.map(record => {
                return [record.timestamp, record.point]
              })
            }
          }
        }

        return {
          ...cd,
          data,
        }
      })
    },

    clearChartDateGivenPlotId(plotId, tabId) {
      this.chartData = this.chartData.map(item => {
        let data = item.data
        let rawData = item.rawData
        if (item.plotId === plotId && item.tabId === tabId) {
          data = []
          rawData = []
        }
        return {
          ...item,
          data,
          rawData,
        }
      })
    },

    applySeparateChartsToChartData(plotId, options) {
      this.chartData = this.chartData.map(item => {
        let chartIndex = item.chartIndex
        if (item.plotId === plotId) {
          chartIndex = options ? item.tabIndex : 0
        }
        return {
          ...item,
          chartIndex,
        }
      })
    },

    handleDuplicatePagePlot(plotId) {
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        // generate new plotId
        const newPlotId = uuidv4()

        // create array of old and new tab ids
        const tabIds = pagePlot.options.data.tabs.map(item => {
          return {
            oldTabId: item.id,
            newTabId: uuidv4(),
          }
        })

        // duplicate page plot
        const dupedPagePlot = _cloneDeep(
          duplicatePagePlot(pagePlot, newPlotId, tabIds),
        )
        this.pagePlots.splice(pagePlot.index, 0, dupedPagePlot)

        // duplicate chart data for duped page plot
        const dupedChartData = duplicateChartData(
          this.chartData,
          plotId,
          newPlotId,
          tabIds,
        )
        this.chartData.push(...dupedChartData)
      }
    },

    handleAddTabToPagePlot(plotId, tabIndex) {
      // get current pagePlot
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        // get currentTab
        const colors = pagePlot.options.data.tabs.map(item => item.iconColor)
        const currentTab = pagePlot.options.data.tabs[tabIndex]
        if (currentTab) {
          pagePlot.dateUpdated = new Date().getTime()
          // dupe currentTab
          const duped = _cloneDeep(currentTab)
          // update new tab
          const updatedTab = {
            ...duped,
            id: uuidv4(),
            hidden: false,
            prepopulated: false, // always false since these are user created
            tabIndex: pagePlot.options.data.tabs.length,
            iconColor: getNextHighchartColor(colors),
          }
          // push updatedTab
          pagePlot.options.data.tabs.push(updatedTab)
          // reset pagePlots
          this.resetPagePlots()
        }
      }
    },

    handleRemoveTabFromPagePlot(plotId, tabIndex) {
      // get current pagePlot
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        // get currentTab
        const currentTab = pagePlot.options.data.tabs[tabIndex]
        if (currentTab) {
          pagePlot.dateUpdated = new Date().getTime()
          // remove from pagePlot.options.data.tabs
          pagePlot.options.data.tabs = pagePlot.options.data.tabs
            .filter(item => item.tabIndex !== tabIndex)
            .map((item, index) => {
              return {
                ...item,
                tabIndex: index,
              }
            })
          // remove from chart data
          this.chartData = this.chartData.filter(
            item => item.tabId !== currentTab.id,
          )
          // reset pagePlots
          this.resetPagePlots()
        }

        // refresh data if the dateCreated and the dateSaved are not equal. This indicates that is has been saved. If they are equal, it has not been saved, thus no need to refresh.
        if (
          Date.parse(pagePlot.dateCreated) !== Date.parse(pagePlot.dateSaved)
        ) {
          const plotTypeId = pagePlot.plotTypeId
          this.handleManualApplyPagePlot(
            plotId,
            plotTypeId,
            PLOT_UPDATE_MESSAGES.updateAllPlotData,
            null,
          )
        }
      }
    },

    getTabHiddenValue(item, plotId, tabId) {
      if (item.plotId === plotId && item.tabId === tabId) {
        return !item.tabHidden
      }
      return item.tabHidden
    },

    handleUpdateTabViewPagePlot(plotId, tabIndex) {
      // get current pagePlot
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        const currentTab = pagePlot.options.data.tabs[tabIndex]
        if (currentTab) {
          pagePlot.dateUpdated = new Date().getTime()
          pagePlot.options.data.tabs = pagePlot.options.data.tabs.map(item => {
            return {
              ...item,
              hidden: item.tabIndex === tabIndex ? !item.hidden : item.hidden,
            }
          })

          // reset pagePlots
          this.resetPagePlots()
          // update chartData
          this.chartData = this.chartData.map(item => {
            return {
              ...item,
              tabHidden: this.getTabHiddenValue(item, plotId, currentTab.id),
            }
          })
        }

        // refresh data
        const plotTypeId = pagePlot.plotTypeId
        this.handleManualApplyPagePlot(
          plotId,
          plotTypeId,
          PLOT_UPDATE_MESSAGES.updateAllPlotData,
          null,
        )
      }
    },

    getTabTitle(tab, fields) {
      const [first] = fields.filter(item => item.id === tab.tabTitleField)
      if (first) {
        if (first.selectedItems.length > 0) {
          return first.selectedItems[0] ? first.selectedItems[0].text : ''
        }
      }
      return 'N/A'
    },

    initPlotChartData(plotId, plotTypeId, tabId) {
      // get page plot given plotId
      const [firstPagePlot] = this.getPagePlots.filter(
        item => item.id === plotId,
      )

      if (!firstPagePlot) {
        return {
          isFound: false,
          items: this.chartData,
          tab: null,
        }
      }

      // get tabs given page plotId
      const tabs = firstPagePlot.options.data.tabs

      // initialize tab containers (with empty data)
      const initChartData = tabs.map((item, index) => {
        return {
          chartIndex: 0,
          data: [],
          flightMappings: {},
          iconColor: item.iconColor,
          keys: [],
          missingTails: [],
          plotId,
          plotTypeId,
          rawData: [],
          seriesTitle: '',
          unit: '',
          tabId: item.id,
          tabIndex: index,
          tabHidden: item.hidden,
        }
      })

      // get chart data items given plotId
      const currentChartData = _cloneDeep(
        this.chartData.filter(item => item.plotId === plotId),
      )

      const [currentTab] = _cloneDeep(tabs.filter(item => item.id === tabId))

      // copy data from existing chart data to initChartData
      const updatedInitChartData = initChartData.map(item => {
        const [first] = _cloneDeep(
          currentChartData.filter(item2 => item2.tabId === item.tabId),
        )
        if (first) {
          return {
            ...item,
            chartIndex: first.chartIndex,
            data: first.data,
            flightMappings: first.flightMappings,
            iconColor: first.iconColor,
            keys: first.keys,
            missingTails: first.missingTails,
            rawData: first.rawData,
            seriesTitle: first.seriesTitle,
            unit: first.unit || '',
          }
        } else {
          return {
            ...item,
          }
        }
      })

      // filter out current chart data
      const remainingChartData = _cloneDeep(
        this.chartData.filter(item => item.plotId !== plotId),
      )

      // merge updated tabs and remaining chart data together
      const updatedChartData = [...updatedInitChartData, ...remainingChartData]

      // remove all items for current tabId from chartData
      const updatedTab = {
        isFound: true,
        items: _cloneDeep(
          updatedChartData.filter(item => item.tabId !== tabId),
        ),
        tab: currentTab,
        iconColor: currentTab.iconColor,
        tabIndex: currentTab.tabIndex,
      }

      return updatedTab
    },

    async handlePlotPreviewAccept() {
      const savedPlot = this.getSavedPlotGivenSavedPlotId()
      const upgradedSavedPlot = this.upgradePagePlot(savedPlot, false, false)

      upgradedSavedPlot.dateUpdated = new Date().getTime()
      const isFound =
        this.pagePlots.filter(item => item.id === this.savedPlotId).length > 0
      this.tabIndex = 0
      if (!isFound && upgradedSavedPlot) {
        const clone = _cloneDeep(upgradedSavedPlot)
        this.pagePlots.unshift(clone)
        await this.handleAutoApplyPagePlot()
      }
      if (isFound && upgradedSavedPlot) {
        this.pagePlots = this.pagePlots.filter(
          item => item.id !== this.savedPlotId,
        )
        this.pagePlots.unshift({ ...upgradedSavedPlot })
      }
    },

    async handleAutoApplyPagePlot() {
      const savedPlot = this.getSavedPlotGivenSavedPlotId()

      if (savedPlot) {
        const clone = _cloneDeep(savedPlot)
        const plotId = clone.id
        const plotTypeId = clone.plotTypeId
        const event = PLOT_UPDATE_MESSAGES.updatePlotData
        const tabIds = clone.options.data.tabs.map(tab => tab.id)
        await this.handleManualApplyPagePlot(plotId, plotTypeId, event, tabIds)
      }
    },

    updateAnalyticFilters(item, analyticModuleIds, featureIds, noGlobalFilter) {
      if (item.id === 'acAnalytic') {
        const [firstId] = analyticModuleIds
        const analyticModules = noGlobalFilter
          ? this.mappedAmcAnalyticModulesAll
          : this.mappedAmcAnalyticModules
        const [analyticModule] = analyticModules.filter(
          item => item.id === firstId,
        )

        return {
          id: 'amcAnalyticModule',
          index: item.index,
          visible: item.visible,
          isDate: item.isDate,
          label: item.label,
          placeholder: item.placeholder,
          selection: analyticModuleIds,
          validationCount: item.validationCount,
          multiple: item.multiple,
          selectedItems: [analyticModule],
        }
      }

      if (item.id === 'acRiskScore') {
        const features = featureIds.map(id => {
          const [feature] = this.mappedAmcFeatures.filter(
            item => item.featureId === id,
          )
          return {
            ...feature,
          }
        })

        return {
          id: 'amcFeature',
          index: item.index,
          visible: item.visible,
          isDate: item.isDate,
          label: item.label,
          placeholder: item.placeholder,
          selection: featureIds,
          validationCount: item.validationCount,
          multiple: item.multiple,
          parentId: 'amcAnalyticModule',
          lookupFields: ['analyticModuleId'],
          selectedItems: features,
        }
      }

      if (item.id === 'tail') {
        return {
          id: item.id,
          index: item.index,
          visible: item.visible,
          isDate: item.isDate,
          label: item.label,
          placeholder: item.placeholder,
          selection: item.selection,
          validationCount: item.validationCount,
          multiple: item.multiple,
          parentId: 'amcAnalyticModule',
          lookupFields: ['masterModelId'],
          lookupArrayFields: item.lookupArrayFields,
          selectedItems: item.selectedItems,
        }
      }

      return item
    },

    updateAnalyticTabSelection(plotTypeId, selection, noGlobalFilter) {
      const [analytic] = selection.filter(item => item.id === 'acAnalytic')
      const [riskScore] = selection.filter(item => item.id === 'acRiskScore')

      if (analytic && riskScore) {
        const riskScoreIds = riskScore.selection
        const analyticModuleIds = []
        for (let index = 0; index < riskScoreIds.length; index++) {
          const id = riskScoreIds[index]
          const [feature] = this.mappedAmcFeatures.filter(
            item => item.riskScoreId === id,
          )
          if (feature) {
            analyticModuleIds.push(feature.analyticModuleId)
          }
        }

        const filters = selection.map(child => {
          const updatedItem = this.updateAnalyticFilters(
            child,
            analyticModuleIds,
            riskScoreIds,
            noGlobalFilter,
          )
          return {
            ...updatedItem,
          }
        })

        if (ANALYTIC_TRENDS_RISK_SCORE_PLOT_TYPE_ID === plotTypeId) {
          const isDateFilterFound =
            filters.filter(item => item.id === 'dates').length > 0
          if (!isDateFilterFound) {
            filters.push({
              id: 'dates',
              index: 3,
              visible: true,
              isDate: true,
              showFixedOptions: true,
              hideOptionsWithId: [6],
              label: 'Dates',
              maxDays: 0,
              selection: {
                dates: {
                  value: '0',
                },
              },
              selectedItems: [],
            })
          }
        }

        return filters
      }

      return selection
    },

    updateAnalyticTabTitleField(id) {
      if (id === 'acRiskScore') {
        return 'amcFeature'
      }

      return id
    },

    updateGeneralTabSelection(selection) {
      const filters = selection.map(item => {
        return {
          ...item,
          selection: this.updateDateSelection(item),
        }
      })

      return filters
    },

    updateDateSelection(selection) {
      if (selection.id === 'dates') {
        const value = selection?.selection?.dates?.value || '0'
        const startDate = selection?.selection?.dates?.startDate || new Date()
        const endDate = selection?.selection?.dates?.endDate || new Date()

        const updatedSelection = {
          dates: {
            value,
            startDate: getDateSelectorStartDateGivenOffset(value, startDate),
            endDate: getDateSelectorEndDateGivenOffset(value, endDate),
          },
        }

        return updatedSelection
      }

      return selection.selection
    },

    upgradePagePlot(savedPlot, noGlobalFilter = false) {
      const upgradedPlot = savedPlot
      const plotTypeId = upgradedPlot.plotTypeId
      const tabs = upgradedPlot.options.data.tabs

      // update if analytic plot
      let updatedTabs = tabs.map(tab => {
        return {
          ...tab,
          selection: this.updateAnalyticTabSelection(
            plotTypeId,
            tab.selection,
            noGlobalFilter,
          ),
          tabTitleField: this.updateAnalyticTabTitleField(tab.tabTitleField),
        }
      })

      // update general tab
      updatedTabs = updatedTabs.map(tab => {
        return {
          ...tab,
          selection: this.updateGeneralTabSelection(tab.selection),
        }
      })

      // update tabs
      upgradedPlot.options.data.tabs = updatedTabs

      return upgradedPlot
    },

    handleUpdatePagePlotGivenNewServerId(plotId, plotServerId) {
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        pagePlot.serverId = plotServerId
        pagePlot.id = plotServerId
      }
    },

    handleUpdateChartDataGivenNewServerId(plotId, plotServerId) {
      this.chartData = this.chartData.map(item => {
        return {
          ...item,
          plotId: item.plotId === plotId ? plotServerId : item.plotId,
        }
      })
    },

    handleUpdateMaintenanceIssueIdsGivenNewServerId(plotId, ids) {
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        pagePlot.maintenanceIssueIds = ids || []
      }
    },

    isDuplicatePlotName(plotId, title) {
      const [pagePlot] = this.savedPlots.filter(
        item =>
          item.id !== plotId &&
          item.title.toLowerCase().trim() === title.toLowerCase().trim(),
      )
      return pagePlot
    },

    handleDupeNameDialogClose() {
      this.dupeNameDialogVisible = false
    },
  },
}
</script>
